import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { FileText, Plus, Eye, Edit, Trash2, ChevronRight, ChevronDown, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TransmittalLog({ user }) {
  const [transmittals, setTransmittals] = useState([]);
  const [filteredTransmittals, setFilteredTransmittals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  
  // Filters
  const [filterProject, setFilterProject] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedTransmittal, setSelectedTransmittal] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    project_id: "",
    transmittal_number: "",
    stage: "Approval",
    description: "",
    submittal_date: "",
    status: "Accepted",
    transmittal_link: "",
    remarks: ""
  });
  
  // Project search
  const [projectSearch, setProjectSearch] = useState("");

  useEffect(() => {
    fetchTransmittals();
    fetchProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transmittals, filterProject, searchTerm]);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('project-dropdown');
      const input = event.target.closest('input[placeholder*="Type to search"]');
      if (dropdown && !dropdown.contains(event.target) && !input) {
        dropdown.style.display = 'none';
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTransmittals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/transmittals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransmittals(response.data);
    } catch (error) {
      console.error("Failed to fetch transmittals:", error);
      toast.error("Failed to load transmittals");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const getDisplayTransmittals = (transmittals) => {
    // Group transmittals by project
    const projectGroups = {};
    
    transmittals.forEach(transmittal => {
      if (!projectGroups[transmittal.project_id]) {
        projectGroups[transmittal.project_id] = {
          project: {
            id: transmittal.project_id,
            project_number: transmittal.project_number,
            project_name: transmittal.project_name
          },
          transmittals: []
        };
      }
      projectGroups[transmittal.project_id].transmittals.push(transmittal);
    });

    // Build display array with grouping
    const result = [];
    Object.values(projectGroups).forEach(group => {
      // Add project header row
      result.push({
        type: 'project',
        project: group.project,
        count: group.transmittals.length
      });

      // Add transmittals if project is expanded
      if (expandedProjects.has(group.project.id)) {
        group.transmittals.forEach(transmittal => {
          result.push({
            type: 'transmittal',
            data: transmittal
          });
        });
      }
    });

    return result;
  };

  const applyFilters = () => {
    let filtered = [...transmittals];

    // Filter by project
    if (filterProject) {
      filtered = filtered.filter(t => t.project_id === filterProject);
    }

    // Search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.full_transmittal_number.toLowerCase().includes(term) ||
        t.description.toLowerCase().includes(term) ||
        t.project_name.toLowerCase().includes(term) ||
        t.project_number.toLowerCase().includes(term)
      );
    }

    setFilteredTransmittals(filtered);
  };

  const handleCreate = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/transmittals`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Transmittal created successfully");
      setIsCreateDialogOpen(false);
      resetForm();
      fetchTransmittals();
    } catch (error) {
      console.error("Failed to create transmittal:", error);
      toast.error(error.response?.data?.detail || "Failed to create transmittal");
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/transmittals/${selectedTransmittal.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Transmittal updated successfully");
      setIsEditDialogOpen(false);
      resetForm();
      fetchTransmittals();
    } catch (error) {
      console.error("Failed to update transmittal:", error);
      toast.error(error.response?.data?.detail || "Failed to update transmittal");
    }
  };

  const handleDelete = async (transmittalId) => {
    if (!window.confirm("Are you sure you want to delete this transmittal?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/transmittals/${transmittalId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Transmittal deleted successfully");
      fetchTransmittals();
    } catch (error) {
      console.error("Failed to delete transmittal:", error);
      toast.error(error.response?.data?.detail || "Failed to delete transmittal");
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setIsCreateDialogOpen(true);
  };

  const openViewDialog = (transmittal) => {
    setSelectedTransmittal(transmittal);
    setIsViewDialogOpen(true);
  };

  const openEditDialog = (transmittal) => {
    setSelectedTransmittal(transmittal);
    setFormData({
      project_id: transmittal.project_id,
      transmittal_number: transmittal.transmittal_number,
      stage: transmittal.stage,
      description: transmittal.description,
      submittal_date: transmittal.submittal_date || "",
      status: transmittal.status,
      transmittal_link: transmittal.transmittal_link || "",
      remarks: transmittal.remarks || ""
    });
    setIsEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      project_id: "",
      transmittal_number: "",
      stage: "Approval",
      description: "",
      submittal_date: "",
      status: "Accepted",
      transmittal_link: "",
      remarks: ""
    });
    setProjectSearch("");
  };

  const handleProjectChange = async (projectId) => {
    setFormData({ ...formData, project_id: projectId });
    
    // Auto-populate next transmittal number for this project
    if (projectId && isCreateDialogOpen) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/transmittals?project_id=${projectId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const existingTransmittals = response.data;
        const nextNumber = (existingTransmittals.length + 1).toString().padStart(3, '0');
        setFormData(prev => ({ ...prev, project_id: projectId, transmittal_number: nextNumber }));
      } catch (error) {
        console.error("Failed to get next transmittal number:", error);
        setFormData(prev => ({ ...prev, project_id: projectId, transmittal_number: "001" }));
      }
    }
  };

  const getFilteredProjects = () => {
    if (!projectSearch) return projects;
    const search = projectSearch.toLowerCase();
    return projects.filter(p => 
      (p.project_number || "").toLowerCase().includes(search) ||
      (p.name || "").toLowerCase().includes(search)
    );
  };

  const getStatusColor = (status) => {
    const colors = {
      "Accepted": "bg-green-100 text-green-800",
      "Rejected with comments/Changes": "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-md3-surface-container text-md3-on-surface";
  };

  const getStageColor = (stage) => {
    const colors = {
      "Approval": "bg-blue-100 text-blue-800",
      "Re-Approval": "bg-yellow-100 text-yellow-800",
      "Fabrication": "bg-purple-100 text-purple-800"
    };
    return colors[stage] || "bg-md3-surface-container text-md3-on-surface";
  };

  const canCreateOrEdit = () => {
    const allowedLevels = ["Team Lead", "Manager", "Admin"];
    return (user?.department === "Projects" && allowedLevels.includes(user?.access_level)) ||
           user?.department === "Management";
  };

  const canDelete = () => {
    return (user?.department === "Projects" && user?.access_level === "Admin") ||
           user?.department === "Management";
  };

  return (
    <Layout user={user}>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-md3-display-small flex items-center gap-2">
              <FileText className="h-8 w-8 text-blue-600" />
              Transmittal Log
            </h1>
            <p className="text-md3-on-surface-variant mt-1">Project Submittal Transmittal Management</p>
          </div>
          <div className="flex gap-2">
            {canCreateOrEdit() && (
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={openCreateDialog}>
                <Plus className="h-4 w-4 mr-2" />
                New Transmittal
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6 shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 ">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Project</Label>
                <Select value={filterProject || "all"} onValueChange={(value) => setFilterProject(value === "all" ? "" : value)}>
                  <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                    <SelectValue placeholder="All Projects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Projects</SelectItem>
                    {projects.map(project => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.project_number} - {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Search</Label>
                <div className="flex gap-2">
                  <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Search by transmittal#, project, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                    onClick={() => {
                      setFilterProject("");
                      setSearchTerm("");
                    }}
                    title="Clear all filters"
                  >
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Transmittal Table */}
        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardContent className="pt-6">
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredTransmittals.length === 0 ? (
              <div className="text-center py-8 text-md3-on-surface-variant">No transmittals found</div>
            ) : (
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Transmittal #</TableHead>
                      <TableHead>Stage</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Submittal Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Link</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getDisplayTransmittals(filteredTransmittals).map((row, index) => {
                      if (row.type === 'project') {
                        const isExpanded = expandedProjects.has(row.project.id);
                        return (
                          <TableRow key={`project-${row.project.id}`} className="bg-md3-surface-container hover:bg-gray-200 font-semibold">
                            <TableCell colSpan={8}>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => toggleProjectExpansion(row.project.id)}
                                  className="hover:bg-gray-300 rounded p-0.5"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="h-4 w-4 text-md3-on-surface-variant" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 text-md3-on-surface-variant" />
                                  )}
                                </button>
                                <span className="text-blue-900">
                                  {row.project.project_number} - {row.project.project_name}
                                </span>
                                <span className="text-md3-on-surface-variant text-md3-body-small font-normal">
                                  ({row.count} transmittal{row.count !== 1 ? 's' : ''})
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        const transmittal = row.data;
                        return (
                          <TableRow key={transmittal.id}>
                            <TableCell className="font-mono font-semibold pl-12">
                              {transmittal.full_transmittal_number}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStageColor(transmittal.stage)}>
                                {transmittal.stage}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-xs truncate">
                              {transmittal.description}
                            </TableCell>
                            <TableCell>
                              {transmittal.submittal_date ? (
                                <div className="flex items-center gap-1 text-md3-body-small">
                                  <Calendar className="h-3 w-3 text-md3-on-surface-variant" />
                                  {new Date(transmittal.submittal_date).toLocaleDateString()}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={getStatusColor(transmittal.status)}>
                                {transmittal.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {transmittal.transmittal_link ? (
                                <a
                                  href={transmittal.transmittal_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:underline text-md3-body-small"
                                >
                                  View
                                </a>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-md3-body-small text-md3-on-surface-variant">
                              {transmittal.created_by_name}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" className="hover:bg-md3-surface-container-high rounded-md3-full transition-all duration-200"
                                  size="sm"
                                  onClick={() => openViewDialog(transmittal)}
                                  title="View Transmittal"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {canCreateOrEdit() && (
                                  <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                    size="sm"
                                    onClick={() => openEditDialog(transmittal)}
                                    title="Edit Transmittal"
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                )}
                                {canDelete() && (
                                  <Button variant="destructive" className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                    size="sm"
                                    onClick={() => handleDelete(transmittal.id)}
                                    title="Delete Transmittal"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      }
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen || isEditDialogOpen} onOpenChange={(open) => {
          setIsCreateDialogOpen(false);
          setIsEditDialogOpen(false);
          if (!open) resetForm();
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">
                {isCreateDialogOpen ? "Create New Transmittal" : "Edit Transmittal"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Project *</Label>
                  {isEditDialogOpen ? (
                    <Input className="bg-md3-surface-container focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" value={`${projects.find(p => p.id === formData.project_id)?.project_number || ""} - ${projects.find(p => p.id === formData.project_id)?.name || ""}`}
                      disabled
                    />
                  ) : (
                    <div className="relative">
                      <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" placeholder="Type to search or select from dropdown..."
                        value={projectSearch}
                        onChange={(e) => {
                          setProjectSearch(e.target.value);
                          const dropdown = document.getElementById('project-dropdown');
                          if (dropdown) dropdown.style.display = 'block';
                        }}
                        onFocus={() => {
                          const dropdown = document.getElementById('project-dropdown');
                          if (dropdown) dropdown.style.display = 'block';
                        }}
                      />
                      <div
                        id="project-dropdown"
                        className="absolute z-50 w-full mt-1 bg-md3-surface-container border border-md3-outline rounded-md3-md shadow-lg max-h-60 overflow-auto hidden"
                        style={{ display: 'none' }}
                      >
                        {getFilteredProjects().length > 0 ? (
                          getFilteredProjects().map(project => (
                            <div
                              key={project.id}
                              className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100"
                              onClick={() => {
                                handleProjectChange(project.id);
                                setProjectSearch(`${project.project_number || ''} - ${project.name || ''}`);
                                document.getElementById('project-dropdown').style.display = 'none';
                              }}
                            >
                              <div className="font-semibold text-blue-900">{project.project_number || 'N/A'}</div>
                              <div className="text-md3-body-small text-md3-on-surface-variant">{project.name || 'Unnamed Project'}</div>
                            </div>
                          ))
                        ) : (
                          <div className="px-3 py-2 text-md3-on-surface-variant text-md3-body-small">No projects found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Transmittal # *</Label>
                  <Input className={`bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 ${isEditDialogOpen ? "bg-md3-surface-container" : ""}`} value={formData.transmittal_number}
                    onChange={(e) => setFormData({ ...formData, transmittal_number: e.target.value })}
                    placeholder="Auto-populated (e.g., 001)"
                    disabled={isEditDialogOpen}
                  />
                  <p className="text-xs text-md3-on-surface-variant mt-1">
                    Format: Project#-T#001
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Stage *</Label>
                  <Select
                    value={formData.stage}
                    onValueChange={(value) => setFormData({ ...formData, stage: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Approval">Approval</SelectItem>
                      <SelectItem value="Re-Approval">Re-Approval</SelectItem>
                      <SelectItem value="Fabrication">Fabrication</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Status *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Accepted">Accepted</SelectItem>
                      <SelectItem value="Rejected with comments/Changes">Rejected with comments/Changes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description *</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the submittal"
                  rows={3}
                />
              </div>
              <div>
                <Label>Submittal Date</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" type="date"
                  value={formData.submittal_date}
                  onChange={(e) => setFormData({ ...formData, submittal_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Transmittal Link</Label>
                <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.transmittal_link}
                  onChange={(e) => setFormData({ ...formData, transmittal_link: e.target.value })}
                  placeholder="https://... (hyperlink to transmittal document)"
                />
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" value={formData.remarks}
                  onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  placeholder="Additional remarks or notes"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                onClick={() => {
                  setIsCreateDialogOpen(false);
                  setIsEditDialogOpen(false);
                  resetForm();
                }}
              >
                Cancel
              </Button>
              <Button className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" onClick={isCreateDialogOpen ? handleCreate : handleUpdate}>
                {isCreateDialogOpen ? "Create Transmittal" : "Update Transmittal"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">Transmittal Details - {selectedTransmittal?.full_transmittal_number}</DialogTitle>
            </DialogHeader>
            {selectedTransmittal && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Project</Label>
                    <p className="font-medium">
                      {selectedTransmittal.project_number} - {selectedTransmittal.project_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Transmittal Number</Label>
                    <p className="font-mono font-semibold">{selectedTransmittal.full_transmittal_number}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Stage</Label>
                    <div>
                      <Badge className={getStageColor(selectedTransmittal.stage)}>
                        {selectedTransmittal.stage}
                      </Badge>
                    </div>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Status</Label>
                    <div>
                      <Badge className={getStatusColor(selectedTransmittal.status)}>
                        {selectedTransmittal.status}
                      </Badge>
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-md3-on-surface-variant ">Description</Label>
                  <p className="mt-1">{selectedTransmittal.description}</p>
                </div>
                {selectedTransmittal.submittal_date && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Submittal Date</Label>
                    <p className="mt-1">{new Date(selectedTransmittal.submittal_date).toLocaleDateString()}</p>
                  </div>
                )}
                {selectedTransmittal.transmittal_link && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Transmittal Link</Label>
                    <a
                      href={selectedTransmittal.transmittal_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline block mt-1"
                    >
                      {selectedTransmittal.transmittal_link}
                    </a>
                  </div>
                )}
                {selectedTransmittal.remarks && (
                  <div>
                    <Label className="text-md3-on-surface-variant ">Remarks</Label>
                    <p className="mt-1">{selectedTransmittal.remarks}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-md3-body-small text-md3-on-surface-variant">
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created By</Label>
                    <p>{selectedTransmittal.created_by_name}</p>
                  </div>
                  <div>
                    <Label className="text-md3-on-surface-variant ">Created At</Label>
                    <p>{new Date(selectedTransmittal.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
